import { trackingService } from "@/lib/tracking";

export const MIN_DECLARED_INCOME = 500;
export const MAX_DECLARED_INCOME = 100_000;

export type IncomeIssueReason =
  | "format"
  | "repeated_digit"
  | "sequence"
  | "repeated_block"
  | "too_low"
  | "too_high";

interface IncomeIssue {
  reason: IncomeIssueReason;
  message: string;
}

const SUSPICIOUS_MESSAGE =
  "Suma introdusă nu pare un venit real. Introdu venitul tău lunar net, în lei.";

const ISSUE_MESSAGES: Record<IncomeIssueReason, string> = {
  format: "Introdu venitul lunar net, în lei, fără zecimale.",
  repeated_digit: SUSPICIOUS_MESSAGE,
  sequence: SUSPICIOUS_MESSAGE,
  repeated_block: SUSPICIOUS_MESSAGE,
  too_low: "Verifică suma: venitul lunar net introdus pare prea mic.",
  too_high: "Verifică suma: venitul lunar net introdus pare prea mare.",
};

// Every digit one step up or down from the previous one: 1234, 98765.
const isSequence = (digits: string) => {
  const step = Number(digits[1]) - Number(digits[0]);
  if (Math.abs(step) !== 1) {
    return false;
  }
  return [...digits]
    .slice(1)
    .every((digit, index) => Number(digit) - Number(digits[index]) === step);
};

// Amounts typed to get past the field rather than read off a payslip.
const getSuspiciousPattern = (digits: string): IncomeIssueReason | null => {
  if (digits.length >= 3 && /^(\d)\1+$/.test(digits)) {
    return "repeated_digit";
  }
  if (digits.length >= 4 && isSequence(digits)) {
    return "sequence";
  }
  if (digits.length >= 6 && /^(\d{2,})\1+$/.test(digits)) {
    return "repeated_block";
  }
  return null;
};

const getIssueReason = (value: string): IncomeIssueReason | null => {
  if (!/^\d+$/.test(value)) {
    return "format";
  }

  const pattern = getSuspiciousPattern(value);
  if (pattern) {
    return pattern;
  }

  const income = Number(value);
  if (income < MIN_DECLARED_INCOME) {
    return "too_low";
  }
  if (income > MAX_DECLARED_INCOME) {
    return "too_high";
  }
  return null;
};

export const getIncomeIssue = (value: string): IncomeIssue | null => {
  const reason = getIssueReason(value.trim());
  return reason ? { reason, message: ISSUE_MESSAGES[reason] } : null;
};

// Repeated or patterned attempts show up as a series of these events.
export const trackIncomeRejected = (
  value: string,
  source: "chat" | "page",
  attempt: number,
) => {
  const issue = getIncomeIssue(value);
  if (!issue) {
    return;
  }

  void trackingService.trackEvent("income_rejected", {
    reason: issue.reason,
    source,
    attempt,
  });
};
